import { type RefObject, useEffect, useMemo, useRef } from 'react';
import clsx from 'clsx';
import { TimePicker as MantineTimePicker, type TimePickerProps } from '@mantine/dates';
import styles from './TimePicker.module.sass';

export const TimePicker = (props: TimePickerProps) => {
  const { scrollAreaProps, classNames: classNamesProp, ...rest } = props;
  const listenersRef = useRef<Map<HTMLElement, () => void>>(new Map());

  useEffect(() => {
    return () => {
      listenersRef.current.forEach((cleanup) => cleanup());
      listenersRef.current.clear();
    };
  }, []);

  const mergedScrollAreaProps = useMemo(
    () => ({
      viewportRef: (node: HTMLDivElement | null) => {
        const viewportRefProp = scrollAreaProps?.viewportRef;

        if (typeof viewportRefProp === 'function') {
          viewportRefProp(node);
        } else if (viewportRefProp) {
          const ref = viewportRefProp as RefObject<HTMLDivElement | null>;
          ref.current = node;
        }

        if (node) {
          if (listenersRef.current.has(node)) return;

          const checkPosition = () => {
            const { scrollHeight, clientHeight, scrollTop } = node;
            const isAtBottom = scrollHeight - scrollTop <= clientHeight + 1;
            const root = node.closest(`.${styles.mantineTimePickerScrollarea}`);

            if (root) {
              if (isAtBottom) {
                root.setAttribute('data-at-bottom', 'true');
              } else {
                root.removeAttribute('data-at-bottom');
              }
            }
          };
          node.addEventListener('scroll', checkPosition);
          checkPosition();
          listenersRef.current.set(node, () => {
            node.removeEventListener('scroll', checkPosition);
          });
        }
      },
      ...scrollAreaProps,
    }),
    [scrollAreaProps]
  );

  const classNamesRecord = typeof classNamesProp === 'function' ? undefined : classNamesProp;

  return (
    <MantineTimePicker
      {...rest}
      classNames={{
        ...classNamesRecord,
        scrollarea: clsx(styles.mantineTimePickerScrollarea, classNamesRecord?.scrollarea),
      }}
      scrollAreaProps={mergedScrollAreaProps}
    />
  );
};
